import Phaser from 'phaser';
import type { ScenarioDefinition, TerrainHex, Unit as UnitData } from '~shared/types';
import { HexBoard } from '../objects/HexBoard';
import { UnitSprite } from '../objects/Unit';

const W = 1440, H = 768;
const BOARD_X = 284;
const BOARD_Y = 95;

/** localStorage key the editor writes the scenario JSON to before opening this tab. */
export const EDITOR_PREVIEW_KEY = 'memoir44_editor_preview';

/**
 * Read-only render of a scenario exactly as the real game would draw it —
 * full HexBoard terrain art (including summer/winter theme) and nation-aware
 * unit sprites — driven entirely by the editor's exported JSON via
 * localStorage, no server/socket round trip. No turn/game logic at all;
 * this is a snapshot of the initial setup, not a playable session.
 */
export class PreviewScene extends Phaser.Scene {
  private board!: HexBoard;
  private unitSprites: UnitSprite[] = [];
  private scenario: ScenarioDefinition | null = null;
  private flipped = false;

  private headerText!: Phaser.GameObjects.Text;
  private subText!:    Phaser.GameObjects.Text;
  private errorText!:  Phaser.GameObjects.Text;
  private flipLabel!:  Phaser.GameObjects.Text;

  constructor() { super({ key: 'PreviewScene' }); }

  create(): void {
    this.add.rectangle(W / 2, H / 2, W, H, 0x1a2d1a);

    // ── Header bar ───────────────────────────────────────────────────
    this.add.rectangle(W / 2, 24, W, 48, 0x0d1a0d).setDepth(1);

    const backBtn = this.add.rectangle(70, 24, 110, 30, 0x243824)
      .setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => backBtn.setFillStyle(0x3a5a3a))
      .on('pointerout',  () => backBtn.setFillStyle(0x243824))
      .on('pointerdown', () => this.backToEditor());
    this.add.text(70, 24, '← Editor', { fontSize: '11px', color: '#aabbaa', fontStyle: 'bold' })
      .setOrigin(0.5).setDepth(3);

    const flipBtn = this.add.rectangle(W - 90, 24, 130, 30, 0x243824)
      .setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => flipBtn.setFillStyle(0x3a5a3a))
      .on('pointerout',  () => flipBtn.setFillStyle(0x243824))
      .on('pointerdown', () => this.toggleFlip());
    this.flipLabel = this.add.text(W - 90, 24, '⇄ Flip board', { fontSize: '11px', color: '#aabbaa', fontStyle: 'bold' })
      .setOrigin(0.5).setDepth(3);

    this.headerText = this.add.text(200, 8,  'MEMOIR \'44 · PREVIEW', {
      fontSize: '13px', color: '#d4af37', fontStyle: 'bold',
    }).setDepth(2);
    this.subText = this.add.text(200, 26, '', { fontSize: '11px', color: '#cccccc' }).setDepth(2);

    this.errorText = this.add.text(W / 2, H / 2, '', {
      fontSize: '16px', color: '#ff8888', align: 'center', wordWrap: { width: 500 },
    }).setOrigin(0.5).setDepth(10).setVisible(false);

    // ── Board ────────────────────────────────────────────────────────
    this.board = new HexBoard(this, BOARD_X, BOARD_Y);

    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    const raw = localStorage.getItem(EDITOR_PREVIEW_KEY);
    // Consumed once — a stale value shouldn't silently reappear on a later visit.
    localStorage.removeItem(EDITOR_PREVIEW_KEY);

    if (!raw) {
      this.showError('No scenario data found.\nOpen this from the editor\'s "Preview" button.');
      return;
    }
    try {
      this.scenario = JSON.parse(raw) as ScenarioDefinition;
    } catch {
      this.showError('Could not parse scenario JSON from the editor.');
      return;
    }
    this.renderScenario();
  }

  private showError(msg: string): void {
    this.errorText.setText(msg).setVisible(true);
  }

  private toggleFlip(): void {
    this.flipped = !this.flipped;
    this.flipLabel.setText(this.flipped ? '⇄ Unflip board' : '⇄ Flip board');
    if (this.scenario) this.renderScenario();
  }

  private backToEditor(): void {
    window.close();
  }

  private renderScenario(): void {
    const scenario = this.scenario;
    if (!scenario) return;

    this.headerText.setText(`MEMOIR '44 · PREVIEW · ${(scenario.name || 'Untitled').toUpperCase()}`);
    const allies = scenario.alliesName || 'Allies';
    const axis   = scenario.axisName   || 'Axis';
    const theme  = scenario.terrainTheme ? ` · ${scenario.terrainTheme}` : '';
    this.subText.setText(`${allies} vs ${axis}${theme} · viewing as ${this.flipped ? axis : allies}`);

    // Merge minefieldHexes into the terrain array so the board marker renders —
    // the editor stores them separately (strength is randomized at game start,
    // not authored here, so a placeholder value is fine: unrevealed always
    // shows "?" regardless of the underlying number).
    const terrain: TerrainHex[] = scenario.terrain.map(t => ({ ...t }));
    for (const m of scenario.minefieldHexes ?? []) {
      let hex = terrain.find(t => t.col === m.col && t.row === m.row);
      if (!hex) { hex = { col: m.col, row: m.row, type: 'clear' }; terrain.push(hex); }
      hex.minefield = { ownerSide: m.side, strength: 0, revealed: false };
    }

    this.board.render(terrain, scenario.specialHexes ?? [], this.flipped, scenario.terrainTheme);

    for (const sprite of this.unitSprites) sprite.destroy();
    this.unitSprites = [];
    for (const u of scenario.units) {
      const data: UnitData = {
        ...u,
        hexesMoved: 0, hasAttacked: false, attackCount: 0, ordered: false,
      };
      this.unitSprites.push(new UnitSprite(this, data, BOARD_X, BOARD_Y, this.flipped, scenario.factions));
    }
  }
}
